/**
 * BroadcastMessagingModal.jsx
 * 
 * A modal dialog for broadcasting messages to ALL active drivers.
 * 
 * FEATURES:
 * - Opens as a modal dialog when clicking "Broadcast" button in the header
 * - Fetches previous broadcast messages from /api/Communication/BroadcastComs
 * - Chat-style UI showing all previous broadcasts
 * - Text input at bottom with send button
 * - Messages sent via SignalR using BroadcastToAllDrivers socket
 * - Auto-scrolls to latest message
 * 
 * PROPS:
 * - isOpen: boolean - Controls modal visibility
 * - onClose: function - Called when modal should close
 * 
 * REAL-TIME:
 * - Broadcasts are sent to all connected drivers via SignalR
 * 
 * DATA STRUCTURE (Communication from server):
 * - id: number
 * - message: string
 * - from: string ("Broadcast")
 * - date: string (ISO date)
 */

import React, { useState, useEffect, useRef, memo } from 'react';
import { formatDateTime as formatTime } from '../utils/dateHelpers';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Typography,
    TextField,
    IconButton,
    Paper,
    CircularProgress,
} from '@mui/material';
import {
    Close as CloseIcon,
    Send as SendIcon,
    Campaign as CampaignIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { messagesAPI } from '../services/apiService';
import soundService from '../services/soundService';
import { useAlert } from '../contexts/AlertContext';
import { safeInvoke } from '../utils/signalRHelper';

/**
 * Isolated input area — has its own local state so keystrokes do NOT
 * re-render the message list above.
 */
const BroadcastInput = memo(({ disabled, onSend, isOpen }) => {
    const [value, setValue] = useState('');
    const inputRef = useRef(null);

    // Focus the input after the dialog's enter animation completes
    useEffect(() => {
        if (isOpen) {
            const timer = setTimeout(() => inputRef.current?.focus(), 200);
            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    const handleSend = (e) => {
        e?.preventDefault();
        const text = value.trim();
        if (!text) return;
        setValue('');
        onSend(text, () => setValue(text)); // restore on error
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend(e);
        }
    };

    return (
        <Box
            component="form"
            onSubmit={handleSend}
            sx={{
                p: 2,
                bgcolor: 'white',
                borderTop: 1,
                borderColor: 'divider',
                display: 'flex',
                gap: 1
            }}
        >
            <TextField
                fullWidth
                placeholder="Type a broadcast message..."
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={disabled}
                size="small"
                multiline
                maxRows={3}
                autoFocus
                inputRef={inputRef}
                sx={{ bgcolor: '#f5f5f5' }}
            />
            <IconButton
                type="submit"
                color="primary"
                disabled={!value.trim() || disabled}
                sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': { bgcolor: 'primary.dark' },
                    '&:disabled': { bgcolor: 'grey.300' }
                }}
            >
                {disabled ? <CircularProgress size={24} color="inherit" /> : <SendIcon />}
            </IconButton>
        </Box>
    );
});

const BroadcastMessagingModal = ({
    isOpen,
    onClose,
}) => {
    const { user, signalRConnection, connectSignalR } = useAuth();
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const messagesEndRef = useRef(null);
    const { showAlert } = useAlert();
    /**
     * Focus input when modal opens
     */
    useEffect(() => {
        if (isOpen) {
            fetchBroadcastMessages();
        }
    }, [isOpen]);

    /**
     * Scroll to bottom when messages change
     */
    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const scrollToBottom = () => {
        setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
    };

    /**
     * Fetch broadcast messages from the API
     */
    const fetchBroadcastMessages = async () => {
        try {
            setLoading(true);
            const broadcastMessages = await messagesAPI.getBroadcastMessages();
            setMessages(Array.isArray(broadcastMessages) ? broadcastMessages : []);
            scrollToBottom();
        } catch (error) {
            console.error('Error fetching broadcast messages:', error);
            setMessages([]);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Send a broadcast message to all drivers via SignalR.
     * Called by BroadcastInput with the text and an optional restore callback.
     */
    const handleSendMessage = async (messageText, onError) => {
        if (!messageText.trim()) return;

        setSending(true);

        try {
            // Send via SignalR - pass parameters separately as the hub method expects (int dispatcherId, string messageText)
            const result = await safeInvoke({
                connection: signalRConnection,
                connectFn: connectSignalR,
                methodName: 'DispatcherBroadcastMessage',
                args: [user?.userId || 0, messageText]
            });

            if (!result.success) {
                throw new Error(result.error);
            }

            // Add to local messages (optimistic update)
            const sentMessage = {
                id: Date.now(),
                message: messageText,
                from: 'Broadcast',
                date: new Date().toISOString(),
            };

            setMessages(prev => [...prev, sentMessage]);

            // Play sound notification for broadcast sent
            soundService.playBroadcastSentSound();

        } catch (error) {
            console.error('Error sending broadcast:', error);
            showAlert('Error', 'Failed to send broadcast. Please try again.', [{ text: 'OK' }], 'error');
            onError?.(); // Restore text in the input field
        } finally {
            setSending(false);
        }
    };


    /**
     * Render a single broadcast message
     */
    const renderMessage = (msg, index) => {
        return (
            <Box
                key={msg.id || index}
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    mb: 1.5,
                }}
            >
                <Paper
                    elevation={1}
                    sx={{
                        p: 1.5,
                        maxWidth: '85%',
                        bgcolor: '#e3f2fd', // Light blue for broadcasts
                        borderRadius: 2,
                        borderTopRightRadius: 0,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <CampaignIcon fontSize="small" color="primary" />
                        <Typography variant="caption" color="primary" fontWeight="bold">
                            Broadcast to All Drivers
                        </Typography>
                    </Box>
                    <Typography
                        variant="body2"
                        sx={{
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word'
                        }}
                    >
                        {msg.message}
                    </Typography>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 0.5, textAlign: 'right' }}
                    >
                        {formatTime(msg.date)}
                    </Typography>
                </Paper>
            </Box>
        );
    };

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    height: '70vh',
                    maxHeight: 600,
                    display: 'flex',
                    flexDirection: 'column'
                }
            }}
        >
            {/* Header */}
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: '#1976d2',
                    color: 'white',
                    py: 1.5
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CampaignIcon />
                    <Typography variant="h6">
                        Broadcast to All Drivers
                    </Typography>
                </Box>
                <IconButton onClick={onClose} sx={{ color: 'white' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            {/* Messages Area */}
            <DialogContent
                sx={{
                    flex: 1,
                    overflow: 'auto',
                    p: 2,
                    bgcolor: '#f5f5f5',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                        <CircularProgress />
                    </Box>
                ) : messages.length === 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                        <CampaignIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                        <Typography color="text.secondary">
                            No broadcast messages yet.
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Send a message to all active drivers.
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ flex: 1 }}>
                        {messages.map(renderMessage)}
                        <div ref={messagesEndRef} />
                    </Box>
                )}
            </DialogContent>

            {/* Input Area */}
            <BroadcastInput disabled={sending} onSend={handleSendMessage} isOpen={isOpen} />
        </Dialog>
    );
};

export default BroadcastMessagingModal;
